import { ActivatedRoute, Router } from '@angular/router';
import { Component, ChangeDetectionStrategy, EventEmitter, Output, ChangeDetectorRef, ViewEncapsulation } from '@angular/core';
import { MessageHandlerService, AuthService } from 'app/core';
import { Store } from '@ngxs/store';
import { UserState } from '@sportlogiq/core/user/user.state';
import { ForgotPasswordNavigationState } from '@sportlogiq/login/login.types';
import { AppRoute } from '@sportlogiq/app-routing.models';
import { FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';

@Component({
    selector: 'change-password',
    templateUrl: './change-password.component.html',
    styleUrls: ['./change-password.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class ChangePasswordComponent {
    @Output() changePasswordSuccessful = new EventEmitter<unknown>();

    protected changePasswordFormGroup = this._formBuilder.group({
        oldPassword: this._formBuilder.control<string>('', {
            validators: [Validators.minLength(8)]
        }),
        password: this._formBuilder.control<string>('', {
            nonNullable: true,
            validators: [Validators.required, Validators.minLength(8)]
        }),
        confirmPassword: this._formBuilder.control<string>('', {
            nonNullable: true,
            validators: [Validators.required, Validators.minLength(8)]
        })
    }, {
        validator: this._inputsMustMatch('password', 'confirmPassword')
    });

    protected success: boolean;
    protected serverError: string;
    protected userLoggedIn = this._store.selectSnapshot(UserState.user);
    protected submitted: boolean;
    protected loading: boolean;

    private _uniqueToken: string;

    constructor(
        private _authService: AuthService,
        private _messageHandler: MessageHandlerService,
        private _activatedRoute: ActivatedRoute,
        private _cd: ChangeDetectorRef,
        private _store: Store,
        private _router: Router,
        private _formBuilder: FormBuilder
    ) {
        // this unique token only has a value when changing the password through the "Forgot Password" functionality.
        this._uniqueToken = this._activatedRoute.snapshot.params.uniqueToken;

        if ( this.userLoggedIn ) {
            this.changePasswordFormGroup.controls.password.setValidators([Validators.required]);
        }
    }

    protected handleSubmitChangePassword() {
        this.submitted = true;
        this.success = false;
        this.serverError = undefined;

        this.changePasswordFormGroup.updateValueAndValidity();

        if (this.changePasswordFormGroup.valid) {
            this.loading = true;
            const username = this._uniqueToken?.split('||')[1];

            this._authService.createNewPassword(this.changePasswordFormGroup.getRawValue(), this._uniqueToken).subscribe(() => {
                this.success = true;
                this.loading = false;
                this.changePasswordSuccessful.emit({ user: username, password: this.changePasswordFormGroup.controls.password.value });
                this._messageHandler.emitSuccess('Password changed successfully!');
                this._cd.markForCheck();
            }, (error) => {
                this.loading = false;

                // redirect to forgot-password if status code is 410
                if (error?.status === 410) {
                    const forgotPasswordNavigationState: ForgotPasswordNavigationState = {
                        status: error.status,
                        message: error.message,
                        email: username,
                    };
                    this._router.navigateByUrl(`/${AppRoute.FORGOT_PASSWORD}`, {
                        state: forgotPasswordNavigationState,
                    });
                    return;
                }

                // todo, make changes to backend so that error are returned in json...
                this.serverError = error._body; // this _body does not exist in the json response
                this._messageHandler.emitError(error.error);
                this._cd.markForCheck();
            });
        }
    }

    /**
     * Custom validator to check that two controls in a FormGroup have the same value.
     * @param controlName The name of the first control (e.g., 'password').
     * @param matchingControlName The name of the second control (e.g., 'confirmPassword').
     */
    private _inputsMustMatch(controlName: string, matchingControlName: string): ValidatorFn {
        // eslint-disable-next-line complexity
        return (formGroup: FormGroup): ValidationErrors | null => {
            const control = formGroup.get(controlName);
            const matchingControl = formGroup.get(matchingControlName);

            // Return null if controls are not found, or if the second control is empty
            // but the first one is also empty (or untouched/pristine logic)
            if (!control || !matchingControl) {
                return null;
            }

            // Set error on matchingControl if validation fails
            if (control.value !== matchingControl.value) {
                matchingControl.setErrors({ mustMatch: true });
                return { mustMatch: true }; // Set error on the FormGroup as well
            } else {
                // Clear the custom error on the matching control if they match
                // You must manually remove the error here if it exists.
                const errors = matchingControl.errors;
                if (errors?.mustMatch) {
                    delete errors.mustMatch;
                    // If there are no other errors, set errors to null
                    matchingControl.setErrors(Object.keys(errors).length === 0 ? null : errors);
                }
            }

            return null; // Return null if controls match
        };
    }
}
